import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Spinner, Table } from 'react-bootstrap';
import {
    AlertCircle,
    Building2,
    ExternalLink,
    RefreshCw,
    Users,
} from 'lucide-react';

import PageHeader from '../../layouts/PageHeader';
import {
    getByraQueue,
    getByraConsent,
    byraTotals,
    openRowCounts,
    clientHasBacklog,
    formatCount,
} from '../../../services/ByraService';

/**
 * Byråöversikt — the accounting-firm cross-client exception queue (design
 * §4.8b, Phase 4 item 4). New sub-page of Matchning (':companyId/matching/byra'),
 * gated by the 'byra_tier' feature via the route guard.
 *
 * One work surface aggregating every consented client's open review-tier lines:
 * a totals header, a clients table (namn, öppna bank/skatt rader, backlog), and
 * a drill-down link per client into that client's own Matchning.
 *
 * Graceful by contract: a 403 (no byrå identity / not entitled) or 404 shows an
 * off-state, never a crash. Consent state for the current company is surfaced.
 */

function TotalTile({ label, value, hint }) {
    return (
        <div className="insights-box" style={{ padding: '14px 16px', flex: '1 1 160px' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', marginTop: 2 }}>{value}</div>
            {hint && <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>{hint}</div>}
        </div>
    );
}

export default function ByraQueuePage() {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [offState, setOffState] = useState(null); // {title, body} when 403/404
    const [error, setError] = useState(null);
    const [queue, setQueue] = useState(null);
    const [consentCount, setConsentCount] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setOffState(null);
        getByraQueue(companyId, { scope: 'firm' })
            .then((data) => {
                if (cancelled) return;
                setQueue(data || { clients: [], totals: null });
            })
            .catch((err) => {
                if (cancelled) return;
                const status = err?.response?.status;
                if (status === 403) {
                    setOffState({
                        title: 'Byråöversikt är inte tillgänglig',
                        body: 'Den här vyn kräver ett byråkonto med tillgång till flera klientföretag.',
                    });
                } else if (status === 404) {
                    setOffState({
                        title: 'Byråöversikt är inte aktiverad',
                        body: 'Funktionen är inte tillgänglig för det här kontot ännu.',
                    });
                } else {
                    setError(err?.response?.data?.message || err.message || 'Kunde inte hämta byråkön');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        // Consent state rides along; failure just hides the consent note.
        getByraConsent(companyId)
            .then((data) => {
                if (cancelled) return;
                setConsentCount(Array.isArray(data?.consent) ? data.consent.length : null);
            })
            .catch(() => { if (!cancelled) setConsentCount(null); });

        return () => { cancelled = true; };
    }, [companyId]);

    useEffect(() => load(), [load, reloadKey]);

    const clients = Array.isArray(queue?.clients) ? queue.clients : [];
    const totals = byraTotals(queue || {});

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PageHeader
                    icon={Building2}
                    title="Byråöversikt"
                    subtitle="Öppna rader per klient."
                    backTo={{ to: `/${companyId}/matching`, label: 'Till Matchning' }}
                    actions={
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setReloadKey((k) => k + 1)}
                            disabled={loading}
                        >
                            <RefreshCw size={14} className={loading ? 'me-1 spin' : 'me-1'} />
                            Uppdatera
                        </Button>
                    }
                />
                {consentCount != null && (
                    <div style={{ fontSize: '0.8125rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Users size={14} />
                        {consentCount === 0
                            ? 'Det här företaget har inte gett någon byrå tillgång ännu.'
                            : `Det här företaget har gett ${consentCount} ${consentCount === 1 ? 'byrå' : 'byråer'} tillgång.`}
                    </div>
                )}

                {loading ? (
                    <div className="insights-box" style={{ padding: 40, textAlign: 'center' }}>
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Laddar klienter…</span>
                    </div>
                ) : offState ? (
                    <div className="insights-box" style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
                        <Building2 size={32} style={{ opacity: 0.4 }} />
                        <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>{offState.title}</p>
                        <p style={{ fontSize: 13, marginBottom: 0 }}>{offState.body}</p>
                    </div>
                ) : error ? (
                    <Alert variant="warning" className="mb-0">
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                ) : (
                    <>
                        {/* Totals header */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <TotalTile label="Klienter" value={formatCount(totals.clients)} />
                            <TotalTile
                                label="Klienter med backlog"
                                value={formatCount(totals.clients_with_backlog)}
                                hint="klienter med öppna rader att hantera"
                            />
                            <TotalTile
                                label="Öppna rader totalt"
                                value={formatCount(totals.open_total)}
                                hint="bank + skatt över alla klienter"
                            />
                        </div>

                        {/* Clients table */}
                        {clients.length === 0 ? (
                            <div className="insights-box" style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
                                <p style={{ marginBottom: 0, fontSize: 13 }}>
                                    Inga klienter med öppna rader just nu.
                                </p>
                            </div>
                        ) : (
                            <div className="insights-box" style={{ overflowX: 'auto' }}>
                                <Table className="mb-0 align-middle" style={{ fontSize: '0.8125rem' }}>
                                    <thead>
                                        <tr style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
                                            <th>Klient</th>
                                            <th className="text-end">Bank</th>
                                            <th className="text-end">Skatt</th>
                                            <th className="text-end">Öppna totalt</th>
                                            <th className="text-center">Backlog</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clients.map((client) => {
                                            const counts = openRowCounts(client);
                                            const backlog = clientHasBacklog(client);
                                            return (
                                                <tr key={client.company_id}>
                                                    <td style={{ fontWeight: 500 }}>
                                                        {client.company_name || client.company_id}
                                                    </td>
                                                    <td className="text-end">{formatCount(counts.bank)}</td>
                                                    <td className="text-end">{formatCount(counts.tax)}</td>
                                                    <td className="text-end" style={{ fontWeight: 600 }}>
                                                        {formatCount(counts.total)}
                                                    </td>
                                                    <td className="text-center">
                                                        {backlog ? (
                                                            <Badge bg="warning" style={{ fontSize: '0.6875rem' }}>Backlog</Badge>
                                                        ) : (
                                                            <Badge bg="success" style={{ fontSize: '0.6875rem' }}>Klar</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            as={Link}
                                                            to={`/${client.company_id}/matching`}
                                                            variant="outline-primary"
                                                            size="sm"
                                                        >
                                                            <ExternalLink size={13} className="me-1" />
                                                            Öppna
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
                .insights-box { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </>
    );
}
